"use client";

import React from "react";
import {
  ArrowLeftIcon,
  CoatHangerIcon,
  MagicWandIcon,
} from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import { useCreatePageStore } from "@/stores/createPageStore";
import { getGeneratedFrontDesignSpec } from "@/lib/designer/savedDesign";
import { compileCurrentDesignSpec } from "@/lib/agent/briefForm";
import FindSimilarDresses from "../FindSimilarDresses";

export type FrontReviewPanel = "main" | "modify" | "back";

/** Search for the dress on screen, even when the customer has pending edits. */
export function FrontReviewAmazonLink() {
  const front = useCreatePageStore(state => state.generation.images.front);
  const hasBackImage = useCreatePageStore(state =>
    Boolean(state.generation.images.back.url)
  );
  const formData = useCreatePageStore(state => state.form.data);
  const designBrief = useCreatePageStore(state => state.agent.designBrief);
  if (!front.url?.trim()) return null;
  const savedSpec = getGeneratedFrontDesignSpec(front);
  return (
    <FindSimilarDresses
      placement="chat_review"
      designSpec={
        savedSpec ??
        compileCurrentDesignSpec(designBrief, formData, {
          hasFrontImage: true,
          hasBackImage,
        })
      }
      usesSavedPreview={Boolean(savedSpec)}
    />
  );
}

// Launchers render at the support tier of the commit stack (see
// CommitActionStack): full-width outline rows beneath the filled hero.
const supportLauncherClass = (disabled: boolean) =>
  cn(
    "flex min-h-11 w-full min-w-0 touch-manipulation items-center justify-start gap-2 rounded-xl border px-3 py-2.5 text-sm font-semibold transition-colors duration-150",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
    !disabled
      ? "border-border bg-card text-foreground hover:border-brand-logo/40 hover:bg-muted/35"
      : "cursor-default border-border/60 bg-muted/25 text-muted-foreground"
  );

export function FrontReviewModifyLauncher({
  disabled,
  onClick,
}: {
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      data-front-review-modify-front
      disabled={disabled}
      onClick={() => !disabled && onClick()}
      className={supportLauncherClass(disabled)}
    >
      <MagicWandIcon
        size={16}
        weight="duotone"
        aria-hidden="true"
        className="shrink-0"
      />
      <span className="min-w-0 text-left break-words">Modify the front</span>
    </button>
  );
}

export function FrontReviewBackLauncher({
  disabled,
  onClick,
}: {
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      data-front-review-design-back
      disabled={disabled}
      onClick={() => !disabled && onClick()}
      className={supportLauncherClass(disabled)}
    >
      <CoatHangerIcon
        size={16}
        weight="duotone"
        aria-hidden="true"
        className="shrink-0"
      />
      <span className="min-w-0 text-left break-words">Design the back</span>
    </button>
  );
}

export function FrontReviewPanelBackButton({
  disabled,
  onClick,
  panel,
  autoFocus = true,
}: {
  disabled: boolean;
  onClick: () => void;
  panel: "modify" | "back";
  autoFocus?: boolean;
}) {
  const dataProps =
    panel === "modify"
      ? { "data-front-review-modify-back": true }
      : { "data-front-review-back-panel-back": true };
  // Panel swaps unmount the button that had focus, dropping keyboard users to
  // <body>; landing focus on the panel's back control keeps them oriented.
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  React.useEffect(() => {
    if (autoFocus) buttonRef.current?.focus({ preventScroll: true });
  }, [autoFocus]);

  return (
    <button
      ref={buttonRef}
      type="button"
      {...dataProps}
      disabled={disabled}
      onClick={() => !disabled && onClick()}
      className={cn(
        "flex min-h-11 w-fit min-w-0 touch-manipulation items-center gap-1.5 rounded-lg px-2 py-1.5 text-[13px] font-semibold transition-colors duration-150",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
        !disabled
          ? "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
          : "cursor-default text-muted-foreground/60"
      )}
    >
      <ArrowLeftIcon size={14} weight="bold" aria-hidden="true" />
      <span className="min-w-0 truncate">Back to options</span>
    </button>
  );
}
